<template>
  <div>
    <v-menu v-model="isMenuOpen">
      <template #activator="{ props: menuProps }">
        <slot name="activator" :props="menuProps">
          <v-btn icon="add" variant="flat" v-bind="menuProps" density="comfortable" />
        </slot>
      </template>

      <template #default>
        <v-list>
          <v-list-subheader>{{ $t("general") }}</v-list-subheader>
          <v-list-item prepend-icon="add" :title="$t('admin.users.user')" @click="openDialog('user')" />
          <v-list-item prepend-icon="add" :title="$t('admin.organizations.organization')" @click="openDialog('organization')" />

          <v-list-subheader>{{ $t("assets.title") }}</v-list-subheader>
          <v-list-item
            prepend-icon="add"
            :title="$t('assets.structure.componentGroups.componentGroup')"
            @click="openDialog('componentGroup')"
          />
          <v-list-item prepend-icon="add" :title="$t('assets.structure.components.component')" @click="openDialog('component')" />
          <v-list-item prepend-icon="add" :title="$t('admin.assets.lookupTypes.lookupType')" @click="openDialog('lookupType')" />
          <v-list-item prepend-icon="add" :title="$t('admin.assets.lookupValues.lookupValue')" @click="openDialog('lookupValue')" />
        </v-list>
      </template>
    </v-menu>

    <v-dialog v-model="organizationDialogOpen" width="600">
      <template #default="{ isActive }">
        <v-card :title="$t('admin.organizations.create')">
          <organization-form
            @submit="
              (org) => {
                organizationDialogOpen = false
                isActive.value = false
                $router.push(`/admin/organizations/${org.type.toLowerCase()}/${org._id}`)
              }
            "
            @cancel="
              () => {
                organizationDialogOpen = false
                isActive.value = false
              }
            "
          />
        </v-card>
      </template>
    </v-dialog>

    <v-dialog v-model="userDialogOpen" width="1000">
      <template #default="{ isActive }">
        <v-card :title="$t('admin.users.create')">
          <user-form
            @ok="
              (user) => {
                userDialogOpen = false
                isActive.value = false
                $router.push(`/admin/users/${user._id}`)
              }
            "
            @cancel="
              () => {
                userDialogOpen = false
                isActive.value = false
              }
            "
          />
        </v-card>
      </template>
    </v-dialog>

    <v-dialog v-model="componentGroupDialogOpen" width="600">
      <template #default="{ isActive }">
        <v-card :title="$t('assets.structure.componentGroups.add')">
          <component-group-form
            @submit="
              (group) => {
                componentGroupDialogOpen = false
                isActive.value = false
                $router.push(`/admin/asset-structure?selectedComponentGroupId=${group._id}`)
              }
            "
            @cancel="
              () => {
                componentGroupDialogOpen = false
                isActive.value = false
              }
            "
          />
        </v-card>
      </template>
    </v-dialog>

    <v-dialog v-model="componentDialogOpen" width="600">
      <template #default="{ isActive }">
        <v-card :title="$t('assets.structure.components.add')">
          <component-form
            @submit="
              (component) => {
                componentDialogOpen = false
                isActive.value = false
                $router.push(`/admin/asset-structure?selectedComponentId=${component._id}`)
              }
            "
            @cancel="
              () => {
                componentDialogOpen = false
                isActive.value = false
              }
            "
          />
        </v-card>
      </template>
    </v-dialog>

    <v-dialog v-model="lookupTypeDialogOpen" width="600">
      <template #default="{ isActive }">
        <v-card :title="$t('admin.assets.lookupTypes.lookupType')">
          <lookup-type-form
            @submit="
              (lookupType) => {
                lookupTypeDialogOpen = false
                isActive.value = false
                $router.push(`/admin/lookup-types/${lookupType._id}`)
              }
            "
            @cancel="
              () => {
                lookupTypeDialogOpen = false
                isActive.value = false
              }
            "
          />
        </v-card>
      </template>
    </v-dialog>

    <v-dialog v-model="lookupValueDialogOpen" width="600">
      <template #default="{ isActive }">
        <v-card :title="$t('admin.assets.lookupValues.lookupValue')">
          <lookup-value-form
            @submit="
              (lookupValue) => {
                lookupValueDialogOpen = false
                isActive.value = false
                $router.push(`/admin/lookup-values/${lookupValue._id}`)
              }
            "
            @cancel="
              () => {
                lookupValueDialogOpen = false
                isActive.value = false
              }
            "
          />
        </v-card>
      </template>
    </v-dialog>

    <Teleport v-if="isMenuOpen && !hideOverlay" to="#add-menu-backdrop-target">
      <div v-if="!isAnyDialogOpen" class="menu-backdrop" @click="isMenuOpen = false" />
    </Teleport>
  </div>
</template>

<script setup lang="ts">
const $router = useRouter()

const props = defineProps<{
  hideOverlay?: boolean
}>()

const { hideOverlay } = toRefs(props)

const isMenuOpen = ref(false)

const organizationDialogOpen = ref(false)
const userDialogOpen = ref(false)
const componentGroupDialogOpen = ref(false)
const componentDialogOpen = ref(false)
const lookupTypeDialogOpen = ref(false)
const lookupValueDialogOpen = ref(false)

const isAnyDialogOpen = computed(
  () =>
    organizationDialogOpen.value ||
    userDialogOpen.value ||
    componentGroupDialogOpen.value ||
    componentDialogOpen.value ||
    lookupTypeDialogOpen.value ||
    lookupValueDialogOpen.value,
)

const openDialog = (type: "user" | "organization" | "componentGroup" | "component" | "lookupType" | "lookupValue") => {
  isMenuOpen.value = false

  if (type === "user") userDialogOpen.value = true
  if (type === "organization") organizationDialogOpen.value = true
  if (type === "componentGroup") componentGroupDialogOpen.value = true
  if (type === "component") componentDialogOpen.value = true
  if (type === "lookupType") lookupTypeDialogOpen.value = true
  if (type === "lookupValue") lookupValueDialogOpen.value = true
}
</script>
